import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { baseUrl } from '../../utils/axios'
import { handleErrors, formatParams, getParams } from 'utils/helpers'
import { AxiosResponse } from 'axios'
import { IPaginatedResponse } from 'types/api/general.interface'
import { ITransportationSlice } from 'types/transportation.interface'
import {
  ITransferPoint,
  ITransportationVehicleRequest,
  ITransportationVehicleSingleResponse,
} from 'types/api/transportationApi.interface'
import { toast } from 'react-toastify'
import { ITransportationVehicleModal } from 'pages/Transportation/TransportationVehicleModal'

const initialState: ITransportationSlice = {
  isLoading: false,
  transferPoints: {
    points: [],
    totalCount: 0,
    totalPages: 0,
    noPoints: true,
  },
  vehicles: {
    vehicles: [],
    totalCount: 0,
    totalPages: 0,
    noVehicles: true,
  },
}

export const getTransferPoints = createAsyncThunk(
  'transportation/getTransferPoints',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<ITransferPoint>> = await baseUrl.get(
        'transportation/management/transfer-point',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const addTransferPoint = createAsyncThunk(
  'transportation/addTransferPoint',
  async (state: ITransferPoint, thunkAPI) => {
    try {
      const resp: AxiosResponse<ITransferPoint> = await baseUrl.post(
        'transportation/management/transfer-point',
        state,
      )
      const params = getParams({
        location,
        limit: 5,
      })
      thunkAPI.dispatch(getTransferPoints(params))
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const editTransferPoint = createAsyncThunk(
  'transportation/editTransferPoint',
  async (state: ITransferPoint, thunkAPI) => {
    const req = structuredClone(state)
    const { id } = req
    delete req.id
    try {
      const resp: AxiosResponse<ITransferPoint> = await baseUrl.patch(
        'transportation/management/transfer-point/' + id,
        req,
      )
      return { ...resp.data, id }
    } catch (error) {
      console.log(error)
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const deleteTransferPoint = createAsyncThunk(
  'transportation/deleteTransferPoint',
  async (id: number, thunkAPI) => {
    try {
      await baseUrl.delete('transportation/management/transfer-point/' + id)
      const params = getParams({
        location,
        limit: 5,
      })
      thunkAPI.dispatch(getTransferPoints(params))
      return id
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getTransportationVehicles = createAsyncThunk(
  'transportation/getTransportationVehicles',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<ITransportationVehicleSingleResponse>> =
        await baseUrl.get('transportation/management/vehicle', { params })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const addTransportationVehicle = createAsyncThunk(
  'transportation/addTransportationVehicle',
  async (state: ITransportationVehicleModal, thunkAPI) => {
    try {
      const req: ITransportationVehicleRequest = {
        vehicleType: state.vehicleType.value,
        machineId: state.machine.value,
      }
      const resp: AxiosResponse = await baseUrl.post('transportation/management/vehicle', req)
      const params = getParams({
        location,
        limit: 5,
      })
      thunkAPI.dispatch(getTransportationVehicles(params))
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const editTransportationVehicle = createAsyncThunk(
  'transportation/editTransportationVehicle',
  async (state: ITransportationVehicleModal, thunkAPI) => {
    try {
      const req: ITransportationVehicleRequest = {
        vehicleType: state.vehicleType.value,
        machineId: state.machine.value,
      }
      const resp: AxiosResponse<ITransportationVehicleSingleResponse> = await baseUrl.patch(
        'transportation/management/vehicle/' + state.id,
        req,
      )
      return { ...resp.data, id: state.id }
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const deleteTransportationVehicle = createAsyncThunk(
  'transportation/deleteTransportationVehicle',
  async (id: number, thunkAPI) => {
    try {
      await baseUrl.delete('transportation/management/vehicle/' + id)
      const params = getParams({
        location,
        limit: 5,
      })
      thunkAPI.dispatch(getTransportationVehicles(params))
      return id
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

const transportationSlice = createSlice({
  name: 'transportation',
  initialState,
  reducers: {
    clearTransportation: (state) => {
      state.transferPoints.points = []
      state.transferPoints.totalCount = 0
      state.transferPoints.totalPages = 0
      state.transferPoints.noPoints = true

      state.vehicles.vehicles = []
      state.vehicles.totalCount = 0
      state.vehicles.totalPages = 0
      state.vehicles.noVehicles = true
    },
  },

  extraReducers: {
    [getTransferPoints.pending.type]: (state) => {
      state.isLoading = true
    },
    [getTransferPoints.fulfilled.type]: (
      state: ITransportationSlice,
      { payload }: { payload: IPaginatedResponse<ITransferPoint> },
    ) => {
      state.isLoading = false
      state.transferPoints.points = payload.data
      state.transferPoints.totalCount = payload.totalCount
      state.transferPoints.totalPages = payload.totalPages
      state.transferPoints.noPoints = state.transferPoints.points.length < 1
    },
    [getTransferPoints.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [addTransferPoint.pending.type]: (state) => {
      state.isLoading = true
    },
    [addTransferPoint.fulfilled.type]: (state: ITransportationSlice) => {
      state.isLoading = false
      state.transferPoints.noPoints = false
      toast.success('Transfer point added successfully')
    },
    [addTransferPoint.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [editTransferPoint.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [editTransferPoint.fulfilled.type]: (state: ITransportationSlice) => {
      state.isLoading = false
      toast.success('Transfer point updated successfully')
    },
    [editTransferPoint.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [deleteTransferPoint.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [deleteTransferPoint.fulfilled.type]: (state: ITransportationSlice) => {
      state.isLoading = false
      toast.success('Transfer point deleted successfully')
    },
    [deleteTransferPoint.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getTransportationVehicles.pending.type]: (state) => {
      state.isLoading = true
    },
    [getTransportationVehicles.fulfilled.type]: (
      state: ITransportationSlice,
      { payload }: { payload: IPaginatedResponse<ITransportationVehicleSingleResponse> },
    ) => {
      state.isLoading = false
      state.vehicles.vehicles = payload.data
      state.vehicles.totalCount = payload.totalCount
      state.vehicles.totalPages = payload.totalPages
      state.vehicles.noVehicles = state.vehicles.vehicles.length < 1
    },
    [getTransportationVehicles.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [addTransportationVehicle.pending.type]: (state) => {
      state.isLoading = true
    },
    [addTransportationVehicle.fulfilled.type]: (state: ITransportationSlice) => {
      state.isLoading = false
      state.vehicles.noVehicles = false
      toast.success('Vehicle added successfully')
    },
    [addTransportationVehicle.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [editTransportationVehicle.pending.type]: (state) => {
      state.isLoading = true
    },
    [editTransportationVehicle.fulfilled.type]: (
      state: ITransportationSlice,
      { payload }: { payload: ITransportationVehicleSingleResponse },
    ) => {
      state.isLoading = false
      state.vehicles.vehicles = state.vehicles.vehicles.map((vehicle) =>
        vehicle.id === payload.id ? payload : vehicle,
      )
      toast.success('Vehicle updated successfully')
    },
    [editTransportationVehicle.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [deleteTransportationVehicle.pending.type]: (state) => {
      state.isLoading = true
    },
    [deleteTransportationVehicle.fulfilled.type]: (state: ITransportationSlice) => {
      state.isLoading = false
      state.vehicles.noVehicles = state.vehicles.vehicles.length < 1
      toast.success('Vehicle deleted successfully')
    },
    [deleteTransportationVehicle.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
  },
})

export const { clearTransportation } = transportationSlice.actions
export default transportationSlice.reducer
